import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Copy, Facebook, Instagram, Share2, ExternalLink, Check,
  Globe, Info, ChevronRight, Download,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Navbar from '../components/Navbar';
import { toast } from '../hooks/use-toast';

const platforms = [
  {
    id: 'facebook',
    name: 'Facebook',
    Icon: Facebook,
    iconColor: 'text-[#1877F2]',
    bg: 'bg-blue-50',
    quickSteps: [
      { t: 'Copy your generated ad text', d: 'Use the Copy button on your saved ad.' },
      { t: 'Go to Facebook and click "Create post"', d: 'From your business Page, not personal profile.' },
      { t: 'Paste the ad copy into the post', d: 'Upload the AI-generated image from your saved ad.' },
      { t: 'Click "Boost Post" for paid reach', d: 'Or just post organically for free to your followers.' },
      { t: 'Set your budget in Meta Ads Manager', d: 'Start with £5–£10/day for local targeting.' },
    ],
    adsManagerUrl: 'https://adsmanager.facebook.com/',
    helpUrl: 'https://www.facebook.com/business/help',
  },
  {
    id: 'instagram',
    name: 'Instagram',
    Icon: Instagram,
    iconColor: 'text-rose-500',
    bg: 'bg-rose-50',
    quickSteps: [
      { t: 'Save the AI-generated image to your phone', d: 'Tap "Copy image" from your saved ad.' },
      { t: 'Open Instagram → tap + → Post', d: 'Use your business/creator account.' },
      { t: 'Select the image, add filter if needed', d: 'Next → paste your AI caption + hashtags.' },
      { t: 'Tap Share → post goes live', d: 'For Reels, use the video version of your ad.' },
      { t: 'Promote the post for more reach', d: 'Tap "Boost" under the post to run it as an ad.' },
    ],
    adsManagerUrl: 'https://adsmanager.facebook.com/',
    helpUrl: 'https://business.instagram.com/getting-started',
  },
];

const gbpSteps = [
  {
    t: 'Verify your Google Business Profile',
    d: 'If you haven’t already, go to business.google.com and verify your business by postcard or phone.',
    url: 'https://business.google.com',
  },
  {
    t: 'Use the ad as a Google Business Post',
    d: 'From your GBP dashboard, click Posts → What’s new or Offer → paste your ad copy + upload the image.',
  },
  {
    t: 'Respond to all reviews (good and bad)',
    d: 'Use our AI Review Responder to reply professionally. Reviews boost local SEO and trust.',
    url: '/reviews',
    internal: true,
  },
  {
    t: 'Add fresh photos monthly',
    d: 'Google rewards active profiles. Upload new product / storefront / staff photos at least monthly.',
  },
  {
    t: 'Enable messaging from Google',
    d: 'Turn on chat so local customers can message you directly from your listing.',
  },
];

const ShareGuide = () => {
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState(null);

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast({ title: 'Copied to clipboard' });
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <div className="min-h-screen bg-[#fdfaf6]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div>
          <Badge variant="outline" className="border-slate-300 text-slate-700 mb-3">
            <Share2 className="w-3 h-3 mr-1" /> Share & Publish
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            How to post your AI ads
          </h1>
          <p className="mt-2 text-slate-600 max-w-2xl">
            Two options: paste the copy into your socials directly, or run it as a paid ad through Meta Ads Manager. Use whichever you’re comfortable with.
          </p>
        </div>

        {/* Quick tip */}
        <div className="mt-6 flex gap-3 bg-blue-50 border border-blue-200 rounded-2xl p-4">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <strong>Prefer not to link your accounts?</strong> No problem — every generated ad has a
            <strong> Copy text</strong> button. Paste the copy + upload the image manually to Facebook, Instagram,
            Google Business Profile, LinkedIn, TikTok, or anywhere else. You keep full control.
          </div>
        </div>

        <Tabs defaultValue="facebook" className="mt-8">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="facebook">Facebook</TabsTrigger>
            <TabsTrigger value="instagram">Instagram</TabsTrigger>
            <TabsTrigger value="gbp">Google Business</TabsTrigger>
            <TabsTrigger value="other">Other platforms</TabsTrigger>
          </TabsList>

          {platforms.map(p => (
            <TabsContent key={p.id} value={p.id} className="mt-5">
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl ${p.bg} flex items-center justify-center`}>
                    <p.Icon className={`w-6 h-6 ${p.iconColor}`} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Post to {p.name}</h2>
                    <p className="text-sm text-slate-500">Copy-paste method — no integration needed.</p>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  {p.quickSteps.map((s, i) => (
                    <div key={i} className="flex gap-3 p-3 rounded-xl bg-slate-50">
                      <div className="w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {i + 1}
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">{s.t}</div>
                        <div className="text-sm text-slate-600 mt-0.5">{s.d}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  <a href={p.adsManagerUrl} target="_blank" rel="noopener noreferrer">
                    <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                      Open Meta Ads Manager <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                  </a>
                  <a href={p.helpUrl} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline">
                      {p.name} help <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                  </a>
                  <Button variant="outline" onClick={() => navigate('/dashboard')}>
                    Go to my saved ads
                  </Button>
                </div>

                {/* Budget tip */}
                <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
                  <strong>💡 Budget tip:</strong> For local small businesses, £5–£10/day usually reaches
                  2,000–4,000 people in a 5-mile radius. Start small, measure clicks &amp; sales, then scale up what works.
                </div>
              </div>
            </TabsContent>
          ))}

          <TabsContent value="gbp" className="mt-5">
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                  <GoogleBadge size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Google Business Profile</h2>
                  <p className="text-sm text-slate-500">Free marketing channel. 5-min to set up properly.</p>
                </div>
              </div>
              <div className="mt-6 space-y-3">
                {gbpSteps.map((s, i) => (
                  <div key={i} className="flex gap-3 p-3 rounded-xl bg-slate-50">
                    <div className="w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">{s.t}</div>
                      <div className="text-sm text-slate-600 mt-0.5">{s.d}</div>
                      {s.url && (
                        <div className="mt-2">
                          {s.internal ? (
                            <Button size="sm" variant="outline" onClick={() => navigate(s.url)}>
                              Open AI Review Responder <ChevronRight className="w-3.5 h-3.5 ml-1" />
                            </Button>
                          ) : (
                            <a href={s.url} target="_blank" rel="noopener noreferrer">
                              <Button size="sm" variant="outline">
                                Open <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                              </Button>
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="other" className="mt-5">
            <div className="bg-white border border-slate-200 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-slate-900">Other platforms</h2>
              <p className="mt-1 text-sm text-slate-500">Our ads work anywhere. Here’s where else small businesses post:</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-3">
                {[
                  { t: 'LinkedIn (B2B businesses)', d: 'Great for professional services, consultants, agencies.' },
                  { t: 'TikTok (videos + captions)', d: 'Use the copy as a hook. Film a 15s video, add AI text overlay.' },
                  { t: 'Nextdoor (hyperlocal)', d: 'UK neighbourhoods — perfect for takeaways, trades, tutors.' },
                  { t: 'WhatsApp Status / Channels', d: 'Post to your existing customer base for repeat business.' },
                  { t: 'Google Ads (Search / Display)', d: 'Use the headline + body as ad copy for Google Ads.' },
                  { t: 'Your website / newsletter', d: 'Drop the ad into your homepage or email blast.' },
                ].map((p, i) => (
                  <div key={i} className="p-4 bg-slate-50 rounded-xl">
                    <div className="font-semibold text-slate-900">{p.t}</div>
                    <div className="text-sm text-slate-600 mt-1">{p.d}</div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-10 text-center">
          <Button onClick={() => navigate('/create')} className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white font-semibold h-11 rounded-xl px-6">
            Create another ad
          </Button>
        </div>
      </div>
    </div>
  );
};

const GoogleBadge = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.7-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3.1 0 5.8 1.2 8 3l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.5c-2 1.4-4.6 2.3-7.5 2.3-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39.6 16.3 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.7 2.1-2.1 4-3.8 5.3l6.5 5.5C42.6 35 44 29.9 44 24c0-1.3-.1-2.7-.4-3.5z"/>
  </svg>
);

export default ShareGuide;
